import { CheckCircle2, FileCheck2, FileSignature, Paperclip, ShieldCheck, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import type { AcceptanceMethod, PaymentAttachment, Quote, QuoteAcceptance } from '../types';
import { AttachmentPreview } from './AttachmentPreview';
import {thaiDate} from '../../shared/thaiDate';

interface QuoteAcceptanceModalProps {
  quote: Quote;
  readOnly?: boolean;
  onClose: () => void;
  onAccept: (acceptance: QuoteAcceptance) => Promise<void>;
}

const methodLabels: Record<AcceptanceMethod, string> = {
  signed_document: 'เอกสารที่ลูกค้าเซ็นแล้ว',
  email: 'อีเมล',
  line: 'LINE / แชต',
  phone: 'โทรศัพท์',
  other: 'อื่น ๆ',
};

const date = (value: string) => new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium' }).format(new Date(value));

const readAttachment = (file: File | undefined): Promise<PaymentAttachment | null> => {
  if (!file) return Promise.resolve(null);
  const allowed = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
  if (!allowed.includes(file.type)) return Promise.reject(new Error('หลักฐานรองรับเฉพาะ PNG, JPG, WebP หรือ PDF'));
  if (file.size > 5 * 1024 * 1024) return Promise.reject(new Error('ไฟล์ต้องมีขนาดไม่เกิน 5 MB'));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type, size: file.size, dataUrl: String(reader.result || '') });
    reader.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'));
    reader.readAsDataURL(file);
  });
};

export function QuoteAcceptanceModal({ quote, readOnly = false, onClose, onAccept }: QuoteAcceptanceModalProps) {
  const stored = quote.acceptance;
  const [acceptedAt, setAcceptedAt] = useState(stored?.acceptedAt ?? thaiDate());
  const [acceptedBy, setAcceptedBy] = useState(stored?.acceptedBy ?? quote.customerContactName ?? '');
  const [acceptedByRole, setAcceptedByRole] = useState(stored?.acceptedByRole ?? '');
  const [method, setMethod] = useState<AcceptanceMethod>(stored?.method ?? 'signed_document');
  const [reference, setReference] = useState(stored?.reference ?? '');
  const [note, setNote] = useState(stored?.note ?? '');
  const [evidenceFile, setEvidenceFile] = useState<File>();
  const [confirmed, setConfirmed] = useState(Boolean(stored?.confirmed));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attachmentPreview, setAttachmentPreview] = useState<{ attachment: PaymentAttachment; label: string } | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!acceptedBy.trim()) { setError('กรุณากรอกชื่อผู้ยอมรับ'); return; }
    if (!confirmed) { setError('กรุณายืนยันว่าได้รับการยอมรับจากลูกค้าแล้ว'); return; }
    setBusy(true);
    setError('');
    try {
      const evidence = await readAttachment(evidenceFile);
      await onAccept({
        acceptedAt,
        acceptedBy: acceptedBy.trim(),
        acceptedByRole: acceptedByRole.trim(),
        method,
        reference: reference.trim(),
        note: note.trim(),
        evidence: evidence ?? stored?.evidence ?? null,
        signature: stored?.signature ?? null,
        recordedAt: new Date().toISOString(),
        quoteVersion: quote.version + 1,
        confirmed: true,
      });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'บันทึกการยอมรับไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  return <><div className="modal-backdrop acceptance-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="modal acceptance-modal" role="dialog" aria-modal="true" aria-labelledby="acceptance-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="modal-header"><div><span className="eyebrow">{readOnly ? 'หลักฐานการยอมรับ' : 'ยืนยันก่อนสร้างใบแจ้งหนี้'}</span><h2 id="acceptance-title">{quote.number}</h2></div><button className="icon-btn" type="button" onClick={onClose} aria-label="ปิด"><X size={20} /></button></header>
      {readOnly && stored ? <div className="acceptance-content">
        <div className="acceptance-success"><CheckCircle2 size={25} /><div><strong>ลูกค้ายอมรับใบเสนอราคาแล้ว</strong><span>บันทึกกับเอกสารเวอร์ชัน {stored.quoteVersion}</span></div></div>
        <dl className="acceptance-details"><div><dt>วันที่ยอมรับ</dt><dd>{date(stored.acceptedAt)}</dd></div><div><dt>ผู้ยอมรับ</dt><dd>{stored.acceptedBy}</dd></div>{stored.acceptedByRole && <div><dt>ตำแหน่ง / บริษัท</dt><dd>{stored.acceptedByRole}</dd></div>}<div><dt>ช่องทาง</dt><dd>{methodLabels[stored.method]}</dd></div>{stored.reference && <div><dt>เลขอ้างอิง</dt><dd>{stored.reference}</dd></div>}{stored.note && <div><dt>หมายเหตุ</dt><dd>{stored.note}</dd></div>}</dl>
        {(stored.evidence || stored.signature) && <div className="acceptance-files">{stored.evidence && <button className="btn ghost small" type="button" onClick={() => setAttachmentPreview({ attachment: stored.evidence!, label: 'หลักฐานการยอมรับ' })}><Paperclip size={15} /> หลักฐานการยอมรับ</button>}{stored.signature && <button className="btn ghost small" type="button" onClick={() => setAttachmentPreview({ attachment: stored.signature!, label: 'ไฟล์ลายเซ็นเดิม' })}><FileSignature size={15} /> ไฟล์ลายเซ็นเดิม</button>}</div>}
        <div className="acceptance-privacy"><ShieldCheck size={17} /><span>ข้อมูลและไฟล์นี้เก็บในอุปกรณ์ของคุณสำหรับแพ็กเกจ Free</span></div>
      </div> : <form className="acceptance-content acceptance-form" onSubmit={submit}>
        <div className="acceptance-intro"><FileCheck2 size={23} /><div><strong>บันทึกว่าลูกค้ายอมรับเอกสารฉบับนี้</strong><p>ระบบจะเก็บการยอมรับและหลักฐานแยกจาก PDF ต้นฉบับ พร้อมล็อกใบเสนอราคาไว้เป็นเวอร์ชันเดิม</p></div></div>
        <div className="field-grid two"><label><span>วันที่ลูกค้ายอมรับ</span><input type="date" value={acceptedAt} onChange={(event) => setAcceptedAt(event.target.value)} required /></label><label><span>ช่องทางการยอมรับ</span><select value={method} onChange={(event) => setMethod(event.target.value as AcceptanceMethod)}>{Object.entries(methodLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label></div>
        <div className="field-grid two"><label><span>ชื่อผู้ยอมรับ</span><input value={acceptedBy} onChange={(event) => setAcceptedBy(event.target.value)} placeholder="ชื่อผู้ติดต่อของลูกค้า" required /></label><label><span>ตำแหน่ง / บริษัท (ถ้ามี)</span><input value={acceptedByRole} onChange={(event) => setAcceptedByRole(event.target.value)} /></label></div>
        <label><span>เลขอ้างอิงหรือข้อความอ้างอิง (ถ้ามี)</span><input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="เช่น หัวข้ออีเมล หรือเลขที่หนังสือ" /></label>
        <label><span>หมายเหตุ (ถ้ามี)</span><textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} /></label>
        <label><span>หลักฐานการยอมรับ</span><input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(event) => setEvidenceFile(event.target.files?.[0])} /><small className="field-hint">แนบ PDF ที่ลูกค้าเซ็นกลับ อีเมล หรือภาพแชต · ไฟล์นี้เก็บแยกและไม่เปลี่ยน PDF ต้นฉบับ · ไม่เกิน 5 MB</small></label>
        <label className="acceptance-confirm"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>ข้าพเจ้าได้ตรวจสอบและบันทึกการยอมรับจากลูกค้าแล้ว</span></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <footer className="acceptance-actions"><button className="btn ghost" type="button" onClick={onClose}>ยกเลิก</button><button className="btn primary" type="submit" disabled={busy}><CheckCircle2 size={17} /> {busy ? 'กำลังบันทึก…' : 'บันทึกการยอมรับ'}</button></footer>
      </form>}
    </section>
  </div>
  {attachmentPreview && <AttachmentPreview attachment={attachmentPreview.attachment} label={attachmentPreview.label} onClose={() => setAttachmentPreview(null)} />}
  </>;
}
